import { memo, useCallback, useMemo, useRef, useState, ReactElement } from 'react';
import { EditOutlined, EyeOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Switch, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { BoxedSwitch, Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, IntermediaryGetAllResponse, UserStatus } from '@pmp/api';

import { columns } from '../tableConfig';
import { Guard, useGuard } from '@ohpen/react-authz';
import { ListTableChange, useListTableState } from '@pmp/hooks';
import { ListProps } from '@pmp/types';
import { formatDate, ListColumnFilters, lockActionTitle } from '@pmp/utils';

const { Text } = Typography;

const entityType = ActionEntityType.BROKERS;
const rowKey = 'id';
const activationStatusKey = 'userStatus';
const pendingActivationFilter = [UserStatus.PENDING];

const isPendingActivationOnly = (filters: ListColumnFilters): boolean => {
  const applied = filters[activationStatusKey];

  return Array.isArray(applied) && applied.length === 1 && applied[0] === UserStatus.PENDING;
};

export const BrokersList = memo(
  ({
    isFetching,
    valueLists,
    data,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onViewChangeLog,
  }: ListProps<IntermediaryGetAllResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });
    const tableState = useListTableState<IntermediaryGetAllResponse>(entityType, rowKey, tableRef, columns);
    const [pendingActivation, setPendingActivation] = useState<boolean>(false);

    const { t } = useTranslation();

    const onAdd = useCallback(() => {
      onCreate();
    }, [onCreate]);

    // The toggle owns no filter of its own: it shows whether the Activation status column filter is
    // exactly Pending, so picking that value in the column header switches it on and anything else off.
    const onChange = useCallback<ListTableChange>(
      (pagination, filters, ...rest) => {
        tableState.onChange?.(pagination, filters, ...rest);
        setPendingActivation(isPendingActivationOnly(filters));
      },
      [tableState],
    );

    // Reset Filters clears the grid without an AntD change, and the remembered view is restored the same
    // way, so both are caught here off the applied snapshot.
    const setActiveFilters = useCallback(
      (activeFilters: boolean) => {
        tableState.setActiveFilters?.(activeFilters);

        const applied = tableRef.current?.getState().filters.byColumn;
        if (applied !== undefined) setPendingActivation(isPendingActivationOnly(applied));
      },
      [tableState],
    );

    const onTogglePendingActivation = useCallback(
      (checked: boolean) => {
        const table = tableRef.current;
        if (table === null) return;

        const value = checked ? pendingActivationFilter : null;

        table.setFilters({ [activationStatusKey]: value });
        // The export reads AntD's filtered snapshot, which only refreshes on an AntD change. Dropping it
        // keeps the export off the rows the last column-filter interaction left behind.
        table.resetFilteredData();
        // Writing the filter through the ref does not notify, and the query string has to follow or the
        // next write applies its stale state back over the grid.
        tableState.onFiltersChange?.({ ...table.getState().filters.byColumn, [activationStatusKey]: value });
        setPendingActivation(checked);
      },
      [tableState],
    );

    const getRowActions: (record: IntermediaryGetAllResponse) => (ReactElement | null)[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
            <Button type="text" aria-label="View intermediary" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['brokers:update']} flags={['enableBrokersCrud']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button type="text" aria-label="Edit intermediary" icon={<EditOutlined key="edit" />} onClick={() => onEdit(record)} disabled={!!record.lockedByActionId} />
            </Tooltip>
          </Guard>,
          // TODO PMP-1813 restore once the active-cases check for intermediaries is implemented
          // <Guard key="delete" permissions={['brokers:delete']} flags={['enableBrokersCrud']}>
          //   <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
          //     <Button type="text" aria-label="Delete intermediary" icon={<DeleteOutlined key="delete" />} onClick={() => onDelete(record)} disabled={!!record.lockedByActionId} />
          //   </Tooltip>
          // </Guard>,
          <Guard key="changeLog" flags={['enableChangeLog']}>
            <Tooltip title={t('lender.changeLog.labels.viewChangeLog')} placement="top">
              <Button
                type="text"
                aria-label="View change log"
                icon={<HistoryOutlined />}
                onClick={() => onViewChangeLog?.(record)}
              />
            </Tooltip>
          </Guard>,
          null,
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, onViewChangeLog, t],
    );

    const tableProps: TableProps<ActionEntityType, IntermediaryGetAllResponse> = useMemo(
      () => ({
        ...tableState,
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        resizableColumns: true,
        loading: isFetching,
        getRowActions,
        showRowSelection,
        showViews: false,
        valueLists,
        onChange,
        setActiveFilters,
      }),
      [tableState, data, getRowActions, isFetching, valueLists, showRowSelection, onChange, setActiveFilters],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
              <BoxedSwitch>
                <Text>{t('lender.brokers.labels.pendingActivation')}</Text>
                <Switch
                  checked={pendingActivation}
                  onChange={onTogglePendingActivation}
                  aria-label={t('lender.brokers.labels.pendingActivation')}
                />
              </BoxedSwitch>
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Intermediaries ${formatDate()}`}
                  sheetName="Intermediaries"
                  disabled={isFetching}
                  exportView
                />
              </Guard>
              <Guard permissions={['brokers:create']} flags={['enableBrokersCrud']}>
                <Button icon={<PlusOutlined />} onClick={onAdd} type="primary">
                  {t('enso.labels.create')}
                </Button>
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="lender.brokers.labels.xBrokersSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                editableFileName
                fileName={`Intermediaries ${formatDate()}`}
                sheetName="Intermediaries"
                disabled={isFetching}
                exportView
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </Table>
    );
  },
);
